import React from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardContent,
  IonItem,
  IonIcon,
  IonLabel,
  IonButton
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import {
  logoGithub,
  mail,
  globe,
  document,
  shield,
  heart,
  star
} from 'ionicons/icons';

import { configService } from '../services/ConfigService';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './About.css';

function About() {
  const config = configService.getConfig();
  const baseMeta = seoService.getPageMeta('home');
  const appInfo = config?.app || {};
  const mascotConfig = config?.mascot || {};
  const history = useHistory();

  const handleRateApp = () => {
    const appId = appInfo.id;
    if (appId) {
      window.open(`https://play.google.com/store/apps/details?id=${appId}`, '_system');
    }
  };
  
  // Get mascot path from config
  const getMascotPath = () => {
    if (mascotConfig.basePath && mascotConfig.states?.idle) {
      return `/assets/media/${mascotConfig.basePath}/${mascotConfig.states.idle}`;
    }
    // Fallback to language-based path
    const language = appInfo.language || 'python';
    return `/assets/media/mascots/${language}/mascot.svg`;
  };

  const openLink = (url) => {
    window.open(url, '_system');
  };

  const navigateTo = (path) => {
    history.push(path);
  };

  return (
    <>
      <SEO
        title="About - Learn Coding App"
        description="Learn about our mission to make coding education fun and accessible for everyone."
        ogImage={baseMeta.ogImage}
      />
      <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/profile" />
          </IonButtons>
          <IonTitle>About</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div className="about-container">
          {/* App Info */}
          <div className="app-header">
            <img 
              src={getMascotPath()}
              alt="App Mascot"
              className="app-logo"
            />
            <h1>{appInfo.name || 'Learn Coding'}</h1>
            <p className="tagline">{appInfo.tagline || 'Code like a wizard! 🧙‍♂️'}</p>
            <p className="version">Version {appInfo.version || '1.0.0'}</p>
          </div>

          {/* Description */}
          <IonCard className="description-card">
            <IonCardContent>
              <p>
                {appInfo.description || 
                  'Learn programming from scratch with fun, visual lessons! Perfect for absolute beginners of all ages.'}
              </p>
            </IonCardContent>
          </IonCard>

          {/* Features */}
          <div className="features-section">
            <h2>Features</h2>
            <div className="features-grid">
              <div className="feature-item">
                <span className="feature-icon">📚</span>
                <span>50+ Lessons</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🎮</span>
                <span>Interactive</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">💻</span>
                <span>Run Code</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🏆</span>
                <span>Earn Badges</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🎨</span>
                <span>Visual Learning</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">📱</span>
                <span>Offline Mode</span>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="links-section">
            <h2>Links</h2>
            
            {appInfo.website && (
              <IonItem button detail onClick={() => openLink(appInfo.website)}>
                <IonIcon icon={globe} slot="start" color="primary" />
                <IonLabel>Website</IonLabel>
              </IonItem>
            )}
            
            <IonItem button detail onClick={() => navigateTo('/privacy-policy')}>
              <IonIcon icon={shield} slot="start" color="success" />
              <IonLabel>Privacy Policy</IonLabel>
            </IonItem>
            
            <IonItem button detail onClick={() => navigateTo('/terms-of-service')}>
              <IonIcon icon={document} slot="start" color="secondary" />
              <IonLabel>Terms of Service</IonLabel>
            </IonItem>
            
            {appInfo.supportEmail && (
              <IonItem button detail onClick={() => openLink(`mailto:${appInfo.supportEmail}`)}>
                <IonIcon icon={mail} slot="start" color="tertiary" />
                <IonLabel>Contact Support</IonLabel>
              </IonItem>
            )}
          </div>

          {/* Rate & Review */}
          <div className="rate-section">
            <h2>Enjoying the app?</h2>
            <p>Help us by leaving a rating on the Play Store!</p>
            <IonButton expand="block" color="warning" onClick={handleRateApp}>
              <IonIcon icon={star} slot="start" />
              Rate Us ⭐
            </IonButton>
          </div>

          {/* Credits */}
          <div className="credits-section">
            <p>Made with <IonIcon icon={heart} color="danger" /> by {appInfo.author || 'Your Name'}</p>
            <p className="copyright">© 2026 All rights reserved</p>
          </div>
        </div>
      </IonContent>
    </IonPage>
    </>
  );
}

export default About;